import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ButtonDS } from "@qivia/ui/src/designSystem/components/Button";
import { useAppSelector } from "../../../redux/hooks";
import {
  selectInitialVehicleRestitution,
  selectVehicleRestitutionPictures,
} from "../vehicleRestitution/vehicleSessionsSlice";
import { Page } from "../../components/Page";
import { VehicleConditionForm } from "../shared/vehicleConditionForm";
import "../../../i18n/i18n";

interface RestitutionCorrectionStepProps {
  mileage: string;
  setMileage: (value: string) => void;
  hasTelemetryMileage: boolean;
  isCabinCleaned: boolean;
  setIsCabinCleaned: Dispatch<SetStateAction<boolean>>;
  isTrunkCleaned: boolean;
  setIsTrunkCleaned: Dispatch<SetStateAction<boolean>>;
  observations: string;
  setObservations: (value: string) => void;
  uploadedPictures: { uuid: string; url: string }[];
  setOldPictures: Dispatch<SetStateAction<{ uuid: string; url: string }[]>>;
  newPhotos: File[];
  setNewPhotos: (files: File[]) => void;
  onReturn: () => void;
  onContinue: () => void;
}

export const RestitutionCorrectionStep = (
  props: RestitutionCorrectionStepProps,
) => {
  const { t } = useTranslation();

  const initialRestitution = useAppSelector(selectInitialVehicleRestitution);
  const initialPictures = useAppSelector(selectVehicleRestitutionPictures);

  const [displayError, setDisplayError] = useState<boolean>(false);

  const { setOldPictures } = props;
  const handleRemoveOldPicture = useCallback(
    (uuid: string) => {
      setOldPictures((prev) => prev.filter((picture) => picture.uuid !== uuid));
    },
    [setOldPictures],
  );

  const isRestitutionCorrected = useMemo(() => {
    if (!initialRestitution || !initialPictures) return false;
    const observations = props.observations === "" ? null : props.observations;
    const mileageChanged =
      !props.hasTelemetryMileage &&
      Number(props.mileage) !== initialRestitution.mileage;
    const scalarsChanged =
      mileageChanged ||
      props.isCabinCleaned !== initialRestitution.isCabinCleaned ||
      props.isTrunkCleaned !== initialRestitution.isTrunkCleaned ||
      observations !== initialRestitution.observations;
    const isThereOldPicturesRemoved =
      props.uploadedPictures.length !== initialPictures.length;
    const newPicturesUploaded = props.newPhotos.length > 0;
    return scalarsChanged || newPicturesUploaded || isThereOldPicturesRemoved;
  }, [
    initialRestitution,
    initialPictures,
    props.mileage,
    props.hasTelemetryMileage,
    props.isCabinCleaned,
    props.isTrunkCleaned,
    props.observations,
    props.newPhotos,
    props.uploadedPictures,
  ]);

  const { onContinue } = props;
  const onSubmit = useCallback(() => {
    setDisplayError(!isRestitutionCorrected);
    if (!isRestitutionCorrected) return;
    onContinue();
  }, [isRestitutionCorrected, onContinue]);

  return (
    <Page
      title={t("restitution.correction.title")}
      returnButton
      returnButtonAction={props.onReturn}
      withoutHeaderSpacer
      helpButton
      bottomButtonBackgroundColor="white"
      withShadow
      bottomButton={
        <ButtonDS
          format="fill"
          buttonType="primary"
          text={t("restitution.button.continue")}
          disabled={!isRestitutionCorrected}
          onClick={onSubmit}
        />
      }
    >
      <VehicleConditionForm
        mileage={props.mileage === "" ? "" : Number(props.mileage)}
        setMileage={(value) => props.setMileage(String(value))}
        hasTelemetryMileage={props.hasTelemetryMileage}
        isCabinCleaned={props.isCabinCleaned}
        setIsCabinCleaned={props.setIsCabinCleaned}
        isTrunkCleaned={props.isTrunkCleaned}
        setIsTrunkCleaned={props.setIsTrunkCleaned}
        observations={props.observations}
        setObservations={props.setObservations}
        photos={props.newPhotos}
        setPhotos={props.setNewPhotos}
        oldPhotos={props.uploadedPictures}
        onRemoveOldPhotos={handleRemoveOldPicture}
        formError={
          displayError && !isRestitutionCorrected
            ? t("restitution.correction.noChange.error") || ""
            : undefined
        }
      />
    </Page>
  );
};
